import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Home, CheckCircle, AlertCircle } from 'lucide-react';
import DynamicForm from './DynamicForm';
import { resolveFormFields, injectPropertyOptions } from './formUtils';
import { usePortfolioFormSubmission, SUBMISSION_STATE } from '../../hooks/useFormSubmission';

/**
 * Format price with commas (e.g., 399000 -> $399,000)
 */
function formatPrice(price) {
    if (price === undefined || price === null || price === '') return 'TBD';
    const n = Number(price);
    if (!isFinite(n)) return String(price);
    return `$${n.toLocaleString()}`;
}

/**
 * Public Portfolio Template
 * Displays agent info, bio, and active listings grid
 */
const PublicPortfolioTemplate = ({ portfolio, properties }) => {
    // Use centralized form submission hook
    const {
        submit: submitContactForm,
        state: contactFormState,
        error: contactFormError,
        isSubmitting: isContactFormSubmitting,
        isSuccess: isContactFormSuccess,
        reset: resetContactForm,
    } = usePortfolioFormSubmission({
        portfolio,
        onSuccess: (result) => {
            console.log('[PublicPortfolioTemplate] Contact form submitted successfully:', result.responseId);
        },
        onError: (error) => {
            console.error('[PublicPortfolioTemplate] Contact form error:', error);
        },
    });

    if (!portfolio) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50">
                <p className="text-stone-600">No portfolio data available</p>
            </div>
        );
    }

    // Resolve display name with several common fallbacks (portfolio may store it under different keys)
    const displayName = portfolio?.displayName || portfolio?.name || portfolio?.display_name || portfolio?.ownerName || 'Real Estate Agent';

    const activeListings = properties && properties.length > 0 ? properties : [];

    const handleContactSubmit = async (payload) => {
        // payload: { responses: { ... } }
        const responses = payload.responses || payload.fields || {};
        await submitContactForm(responses);
    };

    return (
        <div className="bg-white">
            {/* Header hero with overlapping avatar (matches design) */}
            <section className="relative bg-stone-50 pb-12 md:pb-16 lg:pb-20">
                {portfolio.headerImage ? (
                    <div className="w-full h-40 md:h-64 lg:h-80 bg-stone-200 overflow-hidden">
                        <img src={portfolio.headerImage} alt={displayName} className="w-full h-full object-cover" />
                    </div>
                ) : (
                    <div className="w-full h-32 md:h-56 lg:h-64 bg-stone-100" />
                )}

                <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
                    <div className="-mt-16 md:-mt-20 flex flex-col items-center text-center">
                        {/* Avatar */}
                        {portfolio.avatarImage && (
                            <div className="mb-6">
                                <img
                                    src={portfolio.avatarImage}
                                    alt={displayName}
                                    className="w-28 h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-full object-cover border-4 border-white shadow-lg"
                                />
                            </div>
                        )}

                        {/* Name */}
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-2 leading-tight">
                            {displayName}
                        </h1>

                        {/* License */}
                        {portfolio.license && (
                            <p className="text-stone-600 text-xs md:text-sm mb-4">DRE #{portfolio.license}</p>
                        )}

                        {/* Contact Info */}
                        <div className="flex flex-col md:flex-row gap-3 md:gap-6 justify-center mb-1 text-stone-700 text-xs md:text-sm">
                            {portfolio.phone && (
                                <a href={`tel:${portfolio.phone}`} className="flex items-center gap-2 hover:text-blue-600">
                                    <Phone className="w-4 h-4" />
                                    <span>{portfolio.phone}</span>
                                </a>
                            )}
                            {portfolio.email && (
                                <a href={`mailto:${portfolio.email}`} className="flex items-center gap-2 hover:text-blue-600">
                                    <Mail className="w-4 h-4" />
                                    <span>{portfolio.email}</span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            {portfolio.aboutTitle && (
                <section className="py-16 md:py-20 lg:py-24 px-4 md:px-6 lg:px-8 bg-white">
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-stone-900 mb-6 leading-tight">
                                {portfolio.aboutTitle}
                            </h2>
                            {portfolio.aboutBody && (
                                <p className="text-stone-600 text-base md:text-lg leading-relaxed md:leading-relaxed whitespace-pre-wrap">
                                    {portfolio.aboutBody}
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* Active Listings Section */}
            {portfolio.showActiveListings && (
                <section className="py-16 md:py-20 lg:py-24 px-4 md:px-6 lg:px-8 bg-stone-50">
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-sm md:text-base lg:text-base font-normal text-stone-400 mb-10 md:mb-12 uppercase tracking-widest text-center">Active Listings Feed</h2>

                        {activeListings.length === 0 ? (
                            <div className="text-center py-16">
                                <Home className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                <p className="text-stone-500 text-base">No active listings available</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {activeListings.map(property => (
                                    <Link
                                        key={property.id}
                                        to={`/property/${property.id}`}
                                        className="group bg-white rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        {/* Property Image */}
                                        <div className="relative w-full h-64 bg-stone-200 overflow-hidden">
                                            {property.imageUrls && property.imageUrls[0] ? (
                                                <img
                                                    src={property.imageUrls[0]}
                                                    alt={property.address || 'Property'}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-stone-200">
                                                    <Home className="w-16 h-16 text-stone-300" />
                                                </div>
                                            )}
                                            {/* Price Badge - Top Left */}
                                            <div className="absolute top-3 left-3 bg-stone-800 text-white px-3 py-1 rounded-sm font-bold text-sm">
                                                {formatPrice(property.price)}
                                            </div>
                                        </div>

                                        {/* Property Details */}
                                        <div className="p-5">
                                            {/* Address (Primary) */}
                                            <h3 className="text-lg font-bold text-stone-900 mb-2 line-clamp-1">
                                                {property.address || 'Property'}
                                            </h3>

                                            {/* Address2 (Secondary) */}
                                            {property.address2 && (
                                                <p className="text-stone-600 text-xs md:text-sm mb-4 flex items-start gap-1 line-clamp-2">
                                                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                    <span>{property.address2}</span>
                                                </p>
                                            )}

                                            {/* Specs Row with Pipe Separators */}
                                            <div className="text-xs md:text-sm text-stone-600 font-medium">
                                                {[
                                                    property.bedrooms !== undefined && `${property.bedrooms} Beds`,
                                                    property.bathrooms !== undefined && `${property.bathrooms} Baths`,
                                                    property.sqft && `${(Number(property.sqft) && Number(property.sqft).toLocaleString()) || String(property.sqft)} Sq Ft`
                                                ].filter(Boolean).join(' | ')}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section className="py-16 px-4 md:px-6 lg:px-8 bg-white">
                <div className="max-w-2xl mx-auto">
                    <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">CONTACT ME</h2>

                    {/* Success State */}
                    {isContactFormSuccess && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            <div>
                                <p className="text-green-800 font-medium">Thank you for reaching out!</p>
                                <p className="text-green-700 text-sm">We'll get back to you as soon as possible.</p>
                            </div>
                        </div>
                    )}

                    {/* Error State */}
                    {contactFormError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <div>
                                <p className="text-red-800 font-medium">Something went wrong</p>
                                <p className="text-red-700 text-sm">{contactFormError}</p>
                            </div>
                        </div>
                    )}

                    {/* Form - Hide after success */}
                    {!isContactFormSuccess && (
                        (() => {
                            const source = Array.isArray(portfolio?.captureFormFields) && portfolio.captureFormFields.length > 0
                                ? portfolio.captureFormFields
                                : (Array.isArray(portfolio?.customFields) ? portfolio.customFields : []);
                            const resolved = injectPropertyOptions(resolveFormFields(source), properties);
                            return (
                                <DynamicForm
                                    customFields={resolved}
                                    includeDefaults={false}
                                    onSubmit={(payload) => handleContactSubmit(payload)}
                                    submitLabel={isContactFormSubmitting ? 'Submitting...' : 'Submit'}
                                    disabled={isContactFormSubmitting}
                                />
                            );
                        })()
                    )}

                    {/* Reset button after success */}
                    {isContactFormSuccess && (
                        <button
                            onClick={resetContactForm}
                            className="w-full mt-4 py-3 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
                        >
                            Send Another Message
                        </button>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-stone-900 text-stone-300 py-8 px-4 text-center text-sm">
                <p>© 2026 Get Kaeros. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default PublicPortfolioTemplate;
